<template>
  <div class="benchmarks-card-container">
    <ChartBlock v-if="showSummary" :class="error ? 'error' : ''">
      <BenchmarksSummaryCard
        :data="data"
        v-bind="sharedCardProps"
        :shouldBlurValues="shouldBlurValues"
        :rows="tableRows"
        :columns="formattedColumns"
        :error="error"
        :isInPercentages="isInPercentages"
        :sortKey="sortKey"
        :sortOrder="sortOrder"
        :legendLabels="legendLabels"
        :legendDotColors="benchmarksColors"
        v-on="$listeners"
      />
    </ChartBlock>
    <div v-else>
      <ChartBlock class="chart-container">
        <BenchmarksChartCard
          :data="filteredChartData"
          v-bind="sharedCardProps"
          :summary="summary"
          :legendLabels="legendLabels"
          :legendDotColors="benchmarksColors"
        />
      </ChartBlock>
      <ChartBlock>
        <BenchmarksTableCard
          :rows="tableRows"
          :columns="formattedColumns"
          :loading="loading"
          :sortKey="sortKey"
          :sortOrder="sortOrder"
          :isInPercentages="isInPercentages"
          v-on="$listeners"
          @updateSearchTerm="updateSearchTerm"
          @updateCheckedData="updateCheckedData"
        />
      </ChartBlock>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ChartBlock from '../../Common/Charting/Shared/ChartBlock.vue';
import BenchmarksSummaryCard from './BenchmarksSummaryCard/BenchmarksSummaryCard.vue';
import BenchmarksChartCard from './BenchmarksChartCard/BenchmarksChartCard.vue';
import BenchmarksTableCard from './BenchmarksTableCard/BenchmarksTableCard.vue';

export default {
  props: {
    theme: {
      type: String,
      default: 'category',
      validator: v => {
        return ['category', 'brand'].indexOf(v) > -1;
      },
    },
    type: {
      type: String,
      default: 'summary',
      validator: v => {
        return ['summary', 'expanded'].indexOf(v) > -1;
      },
    },
    shouldBlurValues: {
      type: Boolean,
    },
    error: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      required: true,
    },
    summary: {
      type: Object,
    },
    labels: {
      type: Object,
      default: () => {},
    },
    isInPercentages: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: true,
    },
    dimension: {
      type: String,
    },
    sortKey: {
      type: String,
    },
    sortOrder: {
      type: Number,
    },
  },
  data() {
    return {
      searchTerm: '',
      checkedData: [],
      dimensionColumn: {
        name: this.dimension,
        value: 'dimension',
      },
    };
  },
  components: {
    ChartBlock,
    BenchmarksSummaryCard,
    BenchmarksChartCard,
    BenchmarksTableCard,
  },
  computed: {
    ...mapGetters([
      'benchmarksColors',
      'benchmarksChartKeys',
    ]),
    sharedCardProps() {
      return {
        brand: this.brand,
        category: this.category,
        theme: this.theme,
        dimension: this.dimension,
        loading: this.loading,
        labels: this.labels,
        title: this.title,
      };
    },
    showSummary() {
      return this.type === 'summary' || this.shouldBlurValues || this.error;
    },
    decimalPlaces() {
      return this.isTable ? 2 : 1;
    },
    contentColumns() {
      return this.benchmarksChartKeys.map(key => {
        return {
          name: this.view === 'brand' ? 'YOUR BRAND' : this.labels[key.name],
          value: key.value,
        };
      });
    },
    contentColumnsHaveSubcategories() {
      return this.contentColumns.some(({ value }) => {
        return value.startsWith('subcategory');
      });
    },
    formattedColumns() {
      const columns = [this.dimensionColumn, ...this.contentColumns];

      if (!this.contentColumnsHaveSubcategories) {
        columns.push({
          name: 'DIFF',
          value: 'diff',
        });
      }

      return columns;
    },
    tableRows() {
      if (this.loading || this.error) return [];

      return this.data;
    },
    checkedDataDimensions() {
      return this.checkedData.map(d => d.dimension);
    },
    filteredChartData() {
      const filterKey = this.searchTerm && this.searchTerm.toLowerCase();
      let chartData = [...this.data];

      if (filterKey) {
        chartData = chartData.filter(d => {
          const currentDimension = d['dimension'];

          return this.isInSearch(filterKey, currentDimension);
        });
      }

      return chartData.filter(d => this.checkedDataDimensions.includes(d.dimension));
    },
    legendLabels() {
      return Object.keys(this.labels).map(k => {
        return { name: k, value: this.labels[k] };
      });
    },
  },
  methods: {
    updateSearchTerm(newVal) {
      this.searchTerm = newVal;
    },
    updateCheckedData(newVal) {
      this.checkedData = newVal;
    },
    isInSearch(searchTerm, value) {
      return String(value)
        .toLowerCase()
        .indexOf(searchTerm) > -1;
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  & .error.chart-block:hover {
    box-shadow: 0px 0px 3px rgba(34, 52, 72, 0.4);
  }

  & .chart-block {
    margin: 5px 0px;
  }
}

.benchmarks-card-container {
  margin: 24px 0;
}

.chart-container {
  margin-bottom: 24px;
}
</style>
